import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getHistoricoEncuesta } from '../../../domain/api/AlcoholimetriaApi';
import { getDateRange } from '../utils/formatters';

/**
 * Custom hook para consultar el histórico de encuestas por estación EVBI
 * Se ejecuta manualmente al presionar el botón "Filtro"
 * 
 * @returns {Object} { metricas, usuarios, paginacion, loading, error, fetchHistorico }
 * @returns {Object|null} return.metricas - { usuarios_medidos, promedio_utilizacion, total_mediciones }
 * @returns {Array} return.usuarios - Lista de usuarios con mediciones
 * @returns {Object|null} return.paginacion - { page, size, totalElements, totalPages, last }
 * @returns {boolean} return.loading - Estado de carga
 * @returns {string|null} return.error - Mensaje de error si ocurre
 * @returns {Function} return.fetchHistorico - Función para ejecutar la consulta
 */
export const useHistoricoEncuesta = () => {
    const dispatch = useDispatch();
    const [metricas, setMetricas] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [paginacion, setPaginacion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Ejecuta la consulta del histórico de encuestas
     * @param {Object} params
     * @param {string|number} params.stationId - ID de estación(es) EVBI. Puede ser un ID único o múltiples IDs concatenados con "-" (ej: "57-5655-52")
     * @param {number} params.month - Mes (1-12)
     * @param {number} params.year - Año
     * @param {number} [params.page=0] - Página
     * @param {number} [params.size=30] - Tamaño de página
     * @param {string} [params.filterEmpresa=''] - Filtro de empresa (opcional)
     * @param {string} [params.filterArea=''] - Filtro de área (opcional)
     * @param {string} [params.filterCargo=''] - Filtro de cargo (opcional)
     */
    const fetchHistorico = useCallback(async ({ stationId, month, year, page = 0, size = 30, filterEmpresa = '', filterArea = '', filterCargo = '' }) => {
        if (!stationId) {
            setError('Debe seleccionar una estación');
            return;
        }

        if (!month || !year) {
            setError('Debe seleccionar mes y año');
            return;
        }

        const { datestart, dateend } = getDateRange(month, year);

        if (!datestart || !dateend) {
            setError('Error al calcular el rango de fechas');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await getHistoricoEncuesta(dispatch, stationId, datestart, dateend, page, size, filterEmpresa, filterArea, filterCargo);

            if (response?.error) {
                setError('Error al obtener los registros');
                setMetricas(null);
                setUsuarios([]);
                setPaginacion(null);
                return;
            }

            // Métricas
            const metricasData = response?.metricas?.[0] || null;
            setMetricas(metricasData);

            // Usuarios (contenido paginado)
            const pagina = response?.pagina || {};
            setUsuarios(pagina.content || []);
            setPaginacion({
                page: pagina.page || 0,
                size: pagina.size || 0,
                totalElements: pagina.totalElements || 0,
                totalPages: pagina.totalPages || 0,
                last: pagina.last || true,
            });
        } catch (err) {
            console.error('Error fetching histórico encuesta:', err);
            setError('Error al obtener los registros');
            setMetricas(null);
            setUsuarios([]);
            setPaginacion(null);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    return { metricas, usuarios, paginacion, loading, error, fetchHistorico };
};

export default useHistoricoEncuesta;
